import { HttpTransport, DataMapper } from '@estee/elc-http-transport';
import {
    ENDECA_PARAMS,
    WILDCARD_CHAR,
    ENDECA_URL_GUIDEDSEARCH_PREFIX,
    ENDECA_URL_TYPEAHEAD_PREFIX,
    NR_PARAM,
    NR_PARAM_AND,
    CONTENT_PARAM_TRUE,
    CONTENT_PARAM_FALSE,
    PARAM_OR,
    RESULT_TYPE_QUERY
} from '../internal/constants/Search';
import DataMappings from './DataMappings';
import { ISearchPaths, IContentEnabledConfig } from '../internal/interfaces/ISearchInterfaces';

export interface IPayload {
    searchTerm: string;
    wildCard?: boolean;
    queryState?: string;
}

export interface IContentPayload {
    searchTerm: string;
    contentEnabledConfig?: IContentEnabledConfig;
    wildCard?: boolean;
}

export interface ISearchApiSdk {
    httpTransport: HttpTransport;
    assemblerUrl?: string | undefined;
    assemblerName?: string | undefined;
    assemblerPaths?: ISearchPaths | undefined;
    buid: string;
}

export class SearchApiSdk {
    protected httpTransport: HttpTransport;
    protected dataMapper: DataMapper;
    protected assemblerUrl: string | undefined;
    protected assemblerName: string | undefined;
    protected assemblerPaths: ISearchPaths | undefined;
    protected buid: string;

    constructor(props: ISearchApiSdk) {
        this.httpTransport = props.httpTransport;
        this.dataMapper = new DataMapper(DataMappings);
        this.assemblerUrl = props.assemblerUrl;
        this.assemblerName = props.assemblerName;
        this.assemblerPaths = props.assemblerPaths;
        this.buid = props.buid;
    }

    public getSearchResults = async (payload: IPayload) => {
        let endecaUrl = `${ENDECA_URL_GUIDEDSEARCH_PREFIX}`;

        if (!!this.assemblerUrl && !!this.assemblerName && !!this.assemblerPaths) {
            endecaUrl = `${this.assemblerUrl}${this.assemblerPaths.guidedSearch}/${this.assemblerName}`;
        }

        let url = endecaUrl;

        if (payload.queryState) {
            const token = payload.queryState.includes('?') ? '&' : '?';
            url = `${endecaUrl}${payload.queryState}${token}${ENDECA_PARAMS}${encodeURI(
                payload.searchTerm
            )}`;
        } else {
            url = `${url}?${ENDECA_PARAMS}${encodeURI(payload.searchTerm)}`;
        }

        if (payload.wildCard) {
            url = `${url}${WILDCARD_CHAR}`;
        }

        url = `${url}${NR_PARAM_AND}${this.buid},${CONTENT_PARAM_FALSE})`;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response: any = await this.httpTransport.jsonp(url);

        return this.dataMapper.mapResponse('results', response);
    };

    public getContentSearchResults = async (contentPayload: IContentPayload) => {
        const { contentEnabledConfig = {} } = contentPayload;

        let endecaUrl = `${ENDECA_URL_GUIDEDSEARCH_PREFIX}`;

        if (!!this.assemblerUrl && !!this.assemblerName && !!this.assemblerPaths) {
            endecaUrl = `${this.assemblerUrl}${this.assemblerPaths.guidedSearch}/${this.assemblerName}`;
        }

        const paramsList = [];

        if (contentEnabledConfig.enableContentArticle) {
            paramsList.push(RESULT_TYPE_QUERY.ARTICLE_TYPE);
        }
        if (contentEnabledConfig.enableContentTout) {
            paramsList.push(RESULT_TYPE_QUERY.TOUT_TYPE);
        }
        if (contentEnabledConfig.enableContentVideo) {
            paramsList.push(RESULT_TYPE_QUERY.VIDEO_TYPE);
        }
        if (contentEnabledConfig.enableContentBanner) {
            paramsList.push(RESULT_TYPE_QUERY.BANNER_TYPE);
        }

        let url = `${endecaUrl}?${ENDECA_PARAMS}${encodeURI(contentPayload.searchTerm)}`;

        if (contentPayload.wildCard) {
            url = `${url}${WILDCARD_CHAR}`;
        }

        if (Boolean(paramsList.length)) {
            url = `${url}${NR_PARAM_AND}${this.buid},${PARAM_OR}(${paramsList.toString()}))`;
        } else {
            url = `${url}${NR_PARAM_AND}${this.buid},${CONTENT_PARAM_TRUE})`;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const contentResponse: any = await this.httpTransport.jsonp(url);

        return this.dataMapper.mapResponse('results', contentResponse);
    };

    public getTypeaheadResults = async (payload: IPayload) => {
        let endecaUrl = `${ENDECA_URL_TYPEAHEAD_PREFIX}`;

        if (!!this.assemblerUrl && !!this.assemblerName && !!this.assemblerPaths) {
            endecaUrl = `${this.assemblerUrl}${this.assemblerPaths.typeahead}/${this.assemblerName}`;
        }

        const url = `${endecaUrl}?${ENDECA_PARAMS}${encodeURI(payload.searchTerm)}${NR_PARAM}${
            this.buid
        }`;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeaheadResponse: any = await this.httpTransport.jsonp(url);

        return this.dataMapper.mapResponse('typeahead', typeaheadResponse);
    };
}
